import {
  makeGetRequest,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from '../../../utils/api'
import urls from '../../../utils/urls'

export const roadmapAPI = {
  // Get ideas grouped by horizon
  getIdeasByHorizon: async () => {
    const response = await makeGetRequest(
      `${urls.roadmapIdeas}by_horizon/`,
      {},
      true
    )
    if (response.status === 200) {
      return response.data
    }
    throw new Error('Failed to fetch ideas by horizon')
  },

  // Get all ideas
  getAllIdeas: async () => {
    const response = await makeGetRequest(urls.roadmapIdeas, {}, true)
    if (response.status === 200) {
      return response.data
    }
    throw new Error('Failed to fetch ideas')
  },

  // Get single idea
  getIdea: async (id) => {
    const response = await makeGetRequest(
      `${urls.roadmapIdeas}${id}/`,
      {},
      true
    )
    if (response.status === 200) {
      return response.data
    }
    throw new Error('Failed to fetch idea')
  },

  // Create idea
  createIdea: async (data) => {
    const response = await makePostRequest(urls.roadmapIdeas, data, {}, true)
    if (response.status === 201 || response.status === 200) {
      return response.data
    }
    throw new Error('Failed to create idea')
  },

  // Update idea
  updateIdea: async (id, data) => {
    const response = await makePatchRequest(
      `${urls.roadmapIdeas}${id}/`,
      data,
      {},
      true
    )
    if (response.status === 200) {
      return response.data
    }
    throw new Error('Failed to update idea')
  },

  // Delete idea
  deleteIdea: async (id) => {
    const response = await makeDeleteRequest(
      `${urls.roadmapIdeas}${id}/`,
      {},
      true
    )
    if (response.status === 204 || response.status === 200) {
      return true
    }
    throw new Error('Failed to delete idea')
  },

  // Move idea to new horizon
  moveHorizon: async (id, horizon) => {
    const response = await makePostRequest(
      `${urls.roadmapIdeas}${id}/move_horizon/`,
      { horizon },
      {},
      true
    )
    if (response.status === 200) {
      return response.data
    }
    throw new Error('Failed to move idea horizon')
  },

  // Fragment idea
  fragmentIdea: async (id, fragments, parentAction = 'KEEP') => {
    const payload = { fragments }
    // Add parent action if specified (KEEP, ABANDONED, COMPLETED)
    if (parentAction && parentAction !== 'KEEP') {
      payload.parent_action = parentAction
    }
    const response = await makePostRequest(
      `${urls.roadmapIdeas}${id}/fragment/`,
      payload,
      {},
      true
    )
    if (response.status === 201 || response.status === 200) {
      return response.data
    }
    throw new Error('Failed to fragment idea')
  },

  // Create link to goal/project/task
  createLink: async (ideaId, contentTypeId, objectId, linkType, modelName = null) => {
    const payload = {
      idea_id: ideaId,
      object_id: objectId,
      link_type: linkType,
    }
    
    // If ContentType ID is provided, use it; otherwise try model_name
    if (contentTypeId) {
      payload.content_type_id = contentTypeId
    } else if (modelName) {
      payload.model_name = modelName
    } else {
      throw new Error('Either content_type_id or model_name must be provided')
    }

    const response = await makePostRequest(
      urls.roadmapLinks,
      payload,
      {},
      true
    )
    if (response.status === 201 || response.status === 200) {
      return response.data
    }
    throw new Error('Failed to create link')
  },

  // Delete link
  deleteLink: async (linkId) => {
    const response = await makeDeleteRequest(
      `${urls.roadmapLinks}${linkId}/`,
      {},
      true
    )
    if (response.status === 204 || response.status === 200) {
      return true
    }
    throw new Error('Failed to delete link')
  },

  // Get links for an idea
  getIdeaLinks: async (ideaId) => {
    try {
      // Try filtering by idea_id
      const response = await makeGetRequest(
        `${urls.roadmapLinks}?idea_id=${ideaId}`,
        {},
        true
      )
      if (response.status === 200) {
        // Handle paginated response (results) or direct array
        return response.data.results || response.data || []
      }
      // If filtering doesn't work, get all links and filter client-side
      const allLinksResponse = await makeGetRequest(
        urls.roadmapLinks,
        {},
        true
      )
      if (allLinksResponse.status === 200) {
        const allLinks = allLinksResponse.data.results || allLinksResponse.data || []
        return allLinks.filter(link => link.idea === ideaId || link.idea_id === ideaId)
      }
      return []
    } catch (error) {
      console.error('Error fetching links:', error)
      return []
    }
  },

  // Check for pending rollover session
  checkRollover: async () => {
    try {
      // Try POST first (as per backend implementation)
      const response = await makePostRequest(
        `${urls.roadmapRollover}check_pending/`,
        {},
        {},
        true
      )
      if (response.status === 200) {
        return response.data
      }
      // If POST fails, try GET as fallback
      const getResponse = await makeGetRequest(
        `${urls.roadmapRollover}check_pending/`,
        {},
        true
      )
      if (getResponse.status === 200) {
        return getResponse.data
      }
      return { pending: false }
    } catch (error) {
      console.error('Error checking rollover:', error)
      return { pending: false }
    }
  },

  // Apply rollover decision
  applyRolloverDecision: async (sessionId, itemId, decision) => {
    const response = await makePostRequest(
      `${urls.roadmapRollover}${sessionId}/apply_decision/`,
      {
        item_id: itemId,
        decision,
      },
      {},
      true
    )
    if (response.status === 200) {
      return response.data
    }
    throw new Error('Failed to apply rollover decision')
  },

  // Get events for an idea
  getIdeaEvents: async (ideaId) => {
    try {
      const response = await makeGetRequest(
        `${urls.roadmapEvents}?idea=${ideaId}`,
        {},
        true
      )
      if (response.status === 200) {
        return response.data.results || response.data || []
      }
      return []
    } catch (error) {
      console.error('Error fetching events:', error)
      return []
    }
  },

  // Get fragments (child ideas) of an idea
  getIdeaFragments: async (ideaId) => {
    try {
      // Fetch all ideas and filter by parent_idea client-side
      // This ensures we get the correct fragments even if backend doesn't support query filtering
      const response = await makeGetRequest(
        urls.roadmapIdeas,
        {},
        true
      )
      if (response.status === 200) {
        const allIdeas = response.data.results || response.data || []
        // Filter ideas where parent_idea matches the given ideaId
        // Handle both snake_case and camelCase field names
        return allIdeas.filter(idea => {
          const parentId = idea.parent_idea || idea.parentIdea
          return parentId === ideaId || parentId === ideaId.toString()
        })
      }
      return []
    } catch (error) {
      console.error('Error fetching fragments:', error)
      return []
    }
  },
}

